/**
 * Helpers HaaS (Hardware as a Service) — Adendo HaaS v.1.1
 *
 * Funções puras que decidem se uma proposta dispara o Adendo HaaS.
 * Usadas no backend (concat PDF, checkbox de aceite) e no frontend (visual).
 */

import type { PropostaHaasItem, PropostaPremiumModulo } from './types/payloads.js';

/** Versões correntes dos documentos contratuais (capturadas no aceite). */
export const VERSAO_CONTRATO_BASE = '2026.08';
export const VERSAO_ADENDO_HAAS = '1.1';

/**
 * Decide se a proposta tem HaaS contratado de verdade.
 *
 * Regra de negócio: pelo menos 1 item com quantidade >= 1 E valor mensal > 0.
 * Itens vazios ou zerados NÃO disparam o Adendo HaaS.
 *
 * Aceita o array de itens diretamente ou undefined (proposta antiga/sem HaaS).
 */
export function temHaas(itens: PropostaHaasItem[] | undefined): boolean {
  if (!itens || itens.length === 0) return false;
  return itens.some((it) => it.quantidade >= 1 && it.valorMensalUnit > 0);
}

/**
 * Decide se a proposta tem Modulos Premium contratados.
 * Aceita undefined/[] e retorna false.
 */
export function temPremiumModulos(itens: PropostaPremiumModulo[] | undefined): boolean {
  return !!itens && itens.length > 0;
}

/**
 * Calcula horas mensais por modulo Premium baseado em quantidade de estacoes.
 * Regra escalonada (decisao 2026-06-06):
 *   ate 15 estacoes    -> 1h por modulo
 *   16-30 estacoes     -> 2h
 *   31-60 estacoes     -> 3h
 *   61-100 estacoes    -> 4h
 *   101-150 estacoes   -> 5h
 *   acima de 150       -> +1h a cada 50 estacoes (151-200=6h, 201-250=7h, etc)
 *
 * Aplica pra TODOS os modulos Premium ativos com a mesma quantidade.
 */
export function horasPremiumModuloPorEstacoes(estacoes: number): number {
  const e = Math.max(0, Math.floor(estacoes));
  if (e <= 15) return 1;
  if (e <= 30) return 2;
  if (e <= 60) return 3;
  if (e <= 100) return 4;
  if (e <= 150) return 5;
  return 5 + Math.ceil((e - 150) / 50);
}
